import Vue from 'vue';
import { RouteAccess } from '../../tenants/types';
import { MeResponse } from './services/auth.service';

/**
 * Aseguradora del usuario o del item: solo los campos del customer de `/me/` (`MeResponse`). Los campos del usuario
 * se descartan: pueden faltar según la aseguradora y el branding no los usa.
 */
export interface CustomerInfo {
    customer: number | null;
    customer_name: string;
    /** URL S3 firmada con vencimiento: se toma de cada respuesta, nunca se guarda como constante. */
    customer_logo: string;
}

/** Lo que `CustomerContext` necesita del acceso (lo cumple `AccessCoordinator`). */
export interface CustomerAccessView {
    /** Acceso de la ruta activa (reactivo). */
    readonly activeAccess: RouteAccess;
    /** Token de la estrategia activa (en `UrlToken`, el de la URL). */
    getToken(): string | null;
    strategyFor(access: RouteAccess): { isAuthenticated(): boolean } | null;
}

export interface CustomerContextOptions {
    /** `false` si el tenant no usa la aseguradora (logo estático y sin `customers`): nunca pide `/me/`. */
    enabled: boolean;
    access: CustomerAccessView;
    /** `GET /me/` por el cliente HTTP compartido: el `Authorization` lo pone la estrategia de la ruta activa. */
    fetchMe: () => Promise<MeResponse | null>;
    /** `userInfo` de la sesión OAuth (store `auth`, reactivo). */
    sessionUserInfo: () => Partial<MeResponse> | null;
    /** Guarda el `/me/` refrescado de la sesión (store `auth` + `localStorage`). Nunca se usa con el token. */
    saveSessionUserInfo: (userInfo: MeResponse) => void;
}

/** Normaliza una respuesta de `/me/` (o el `userInfo` guardado) a `CustomerInfo`. */
export const toCustomerInfo = (raw: Partial<MeResponse> | null | undefined): CustomerInfo | null => {
    if (!raw) {
        return null;
    }
    const customer = typeof raw.customer === 'number' ? raw.customer : null;
    const customerName = raw.customer_name || '';
    const customerLogo = raw.customer_logo || '';
    if (customer === null && !customerName && !customerLogo) {
        return null;
    }
    return { customer, customer_name: customerName, customer_logo: customerLogo };
};

/**
 * Aseguradora vigente según el acceso de la ruta activa (reactivo: el header y el branding la siguen):
 * - `UrlToken`: `/me/` pedido con el token de la URL. Vive solo en memoria: NUNCA se escribe en el store OAuth ni
 *   en `localStorage` (`user_info`), así no se filtra a las vistas de sesión.
 * - `Session` / `Public`: el `userInfo` de la sesión si hay sesión; si no, ninguna.
 *
 * `refresh()` se llama después de cada navegación (`router.afterEach`): en `UrlToken` pide `/me/` cuando cambia el
 * token; en `Session` refresca una vez por carga el `userInfo` guardado (el logo es una URL firmada que vence).
 */
export class CustomerContext {
    private readonly state = Vue.observable({
        token: null as string | null,
        tokenCustomer: null as CustomerInfo | null,
        /** `/me/` con el token de `token` en curso. */
        tokenPending: false
    });
    private sessionRefreshed = false;

    constructor(private readonly options: CustomerContextOptions) {}

    get enabled(): boolean {
        return this.options.enabled;
    }

    /** Aseguradora de la ruta activa, o `null`. */
    get customer(): CustomerInfo | null {
        if (!this.options.enabled) {
            return null;
        }
        if (this.options.access.activeAccess === RouteAccess.UrlToken) {
            return this.state.tokenCustomer;
        }
        return this.sessionCustomer;
    }

    /** Id de la aseguradora de la ruta activa (para los colores), o `null`. */
    get customerId(): number | null {
        const customer = this.customer;
        return customer ? customer.customer : null;
    }

    /**
     * `true` cuando la aseguradora de la ruta activa ya se conoce (o no se pudo pedir: queda `null`). Reactivo.
     * Solo puede ser `false` en una ruta `UrlToken` mientras el `/me/` de su token no respondió (o todavía no se
     * pidió). Deshabilitado, `Session` y `Public` → siempre `true` (la sesión sale del store, sin esperar).
     */
    get resolved(): boolean {
        if (!this.options.enabled || this.options.access.activeAccess !== RouteAccess.UrlToken) {
            return true;
        }
        return this.state.token === this.options.access.getToken() && !this.state.tokenPending;
    }

    /** Aseguradora de la sesión OAuth guardada (si hay sesión), sin mirar la ruta activa. */
    get sessionCustomer(): CustomerInfo | null {
        if (!this.options.enabled) {
            return null;
        }
        const session = this.options.access.strategyFor(RouteAccess.Session);
        if (!session || !session.isAuthenticated()) {
            return null;
        }
        return toCustomerInfo(this.options.sessionUserInfo());
    }

    /** Vuelve a resolver la fuente según el acceso activo. Devuelve la promesa del `/me/` (si lo pidió). */
    refresh(): Promise<void> {
        if (!this.options.enabled) {
            return Promise.resolve();
        }
        const access = this.options.access.activeAccess;
        if (access === RouteAccess.UrlToken) {
            return this.refreshTokenCustomer(this.options.access.getToken());
        }
        if (access === RouteAccess.Session) {
            return this.refreshSessionCustomer();
        }
        return Promise.resolve();
    }

    private refreshTokenCustomer(token: string | null): Promise<void> {
        if (token === this.state.token) {
            return Promise.resolve();
        }
        this.state.token = token;
        this.state.tokenCustomer = null;
        if (!token) {
            this.state.tokenPending = false;
            return Promise.resolve();
        }
        this.state.tokenPending = true;
        // La request sale ahora, con la ruta por token activa: el interceptor pone `Bearer <token de la URL>`.
        return this.options
            .fetchMe()
            .then(response => {
                if (this.state.token === token) {
                    this.state.tokenCustomer = toCustomerInfo(response);
                }
            })
            .catch(error => {
                // Token inválido o `/me/` sin soporte: header con los colores del tenant y sin logo.
                console.error('No se pudo obtener la aseguradora del item (/me/):', error);
            })
            .then(() => {
                if (this.state.token === token) {
                    this.state.tokenPending = false;
                }
            });
    }

    private refreshSessionCustomer(): Promise<void> {
        if (this.sessionRefreshed) {
            return Promise.resolve();
        }
        const session = this.options.access.strategyFor(RouteAccess.Session);
        if (!session || !session.isAuthenticated()) {
            return Promise.resolve();
        }
        this.sessionRefreshed = true;
        return this.options
            .fetchMe()
            .then(userInfo => {
                if (userInfo) {
                    this.options.saveSessionUserInfo(userInfo);
                }
            })
            .catch(error => {
                // Se sigue mostrando el logo/nombre guardado.
                console.error('Failed to fetch user info:', error);
                this.sessionRefreshed = false;
            });
    }
}
